/**
 * Free scarf launch offer.
 *
 * Adds the shopper's chosen scarf to the cart as its own line item alongside the bag, so Shopify
 * reserves and decrements the scarf's stock the same way it would for a sale. The automatic
 * "Buy X get Y" discount in the admin is what makes that line $0.
 *
 * The scarf is added after the bag rather than before it. Horizon registers its own delegated
 * submit handling on document with { capture: true } before this module loads, so there is no
 * way to get in front of the bag add — and reacting to it afterwards is the safer order anyway:
 * a bag that fails to add can never leave an orphaned scarf behind in the cart.
 *
 * If the discount is off or expired the scarf would be charged at full price, so every add is
 * checked against the cart afterwards and pulled back out if it did not come through at zero.
 */

const GIFT_FLAG = '_scarf_offer';
// Matches ThemeEvents.cartUpdate in assets/events.js, spelled out so this file stays importless.
const CART_UPDATE = 'cart:update';
// Marks the cart events this component raises itself, so it never reacts to its own work.
const SOURCE_ID = 'scarf-offer';

class ScarfOfferComponent extends HTMLElement {
  #busy = false;

  connectedCallback() {
    document.addEventListener(CART_UPDATE, this.#onCartUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener(CART_UPDATE, this.#onCartUpdate);
  }

  #onCartUpdate = async (event) => {
    if (this.#busy) return;

    const detail = event.detail;
    const data = detail?.data;
    if (!data || detail.sourceId === SOURCE_ID) return;
    if (data.didError) return;

    // Only react to this page's bag being added, not to any other cart activity.
    if (data.source !== 'product-form-component') return;
    if (String(data.productId) !== String(this.dataset.bagProductId)) return;

    const chosen = this.querySelector('.scarf-offer__input:checked');
    if (!chosen) return;

    const quantity = Number(data.itemCount) > 0 ? Number(data.itemCount) : 1;

    this.#busy = true;
    try {
      this.#clearMessage();
      const keys = await this.#addScarf(chosen, quantity);
      if (keys.length) await this.#removeIfCharged(keys);
    } finally {
      this.#busy = false;
    }
  };

  /** @returns {Promise<string[]>} the cart line keys that were added */
  async #addScarf(chosen, quantity) {
    const variantId = Number(chosen.dataset.variantId);
    if (!variantId) return [];

    const properties = { [GIFT_FLAG]: this.dataset.bagProductId };
    const note = this.dataset.giftNote;
    if (note) properties[this.dataset.giftProperty] = note;

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity, properties }] }),
      });

      if (!response.ok) throw new Error(`Cart add failed with ${response.status}`);

      const result = await response.json();
      this.#refreshCart();
      return (result.items || []).map((item) => item.key);
    } catch (error) {
      // Losing the scarf must never cost the bag sale, so leave the bag alone and say so.
      console.warn('[scarf-offer] could not add the free scarf:', error);
      this.#showMessage(this.dataset.errorUnavailable);
      return [];
    }
  }

  /**
   * The discount is the only thing making the scarf free. If it is not applying, take the scarf
   * back out rather than let the shopper pay full price for something the page called free.
   */
  async #removeIfCharged(keys) {
    let cart;
    try {
      cart = await fetch(`${Theme.routes.cart_url}.js`).then((response) => response.json());
    } catch (error) {
      console.error('[scarf-offer] could not read the cart back:', error);
      return;
    }

    const charged = cart.items.filter((item) => keys.includes(item.key) && item.final_line_price > 0);
    if (!charged.length) return;

    try {
      for (const item of charged) {
        await fetch(Theme.routes.cart_change_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: item.key, quantity: 0 }),
        });
      }

      console.error(
        '[scarf-offer] the free scarf was charged, so it was removed. Check the "Buy X get Y" automatic discount is active.'
      );
      this.#showMessage(this.dataset.errorNotFree);
      this.#refreshCart();
    } catch (error) {
      console.error('[scarf-offer] could not remove the charged scarf:', error);
    }
  }

  /** Nudges Horizon to re-render the cart drawer so the scarf line shows. */
  #refreshCart() {
    const refresh = new Event(CART_UPDATE, { bubbles: true });
    // Horizon's cart-items handler reads event.detail.data.sections and re-renders the section
    // from the server when it is absent, which is what we want here.
    refresh.detail = { resource: null, sourceId: SOURCE_ID, data: {} };
    document.dispatchEvent(refresh);
  }

  #showMessage(text) {
    const message = this.querySelector('.scarf-offer__message');
    if (!message || !text) return;
    message.textContent = text;
    message.classList.remove('hidden');
  }

  #clearMessage() {
    const message = this.querySelector('.scarf-offer__message');
    if (!message) return;
    message.textContent = '';
    message.classList.add('hidden');
  }
}

if (!customElements.get('scarf-offer-component')) {
  customElements.define('scarf-offer-component', ScarfOfferComponent);
}
